function areStylesEqual(a, b) {
	if (a === b) {
		return true;
	}

	if (!a || !b) {
		return false;
	}

	const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
	for (const key of keys) {
		if ((a[key] || "") !== (b[key] || "")) {
			return false;
		}
	}

	return true;
}

function ChatMessageItemActionsMenu({
	isSource,
	isReplyMessage,
	canReply,
	isReplySelected,
	onReply,
	replyLabel,
	anchorVersion
}) {
	const actionRef = React.useRef(null);
	const [inlineStyle, setInlineStyle] = React.useState(null);
	const scopeRef = React.useRef(null);
	const lastAnchorRef = React.useRef(null);
	const retryTimeoutRef = React.useRef(null);
	const [isTouchActive, setIsTouchActive] = React.useState(false);
	const touchHideTimeoutRef = React.useRef(null);
	const longPressTimeoutRef = React.useRef(null);
	const touchStartPositionRef = React.useRef(null);
	const isTouchActiveRef = React.useRef(false);

	const updatePosition = React.useCallback(() => {
		if (typeof window === "undefined") {
			return;
		}

		const actionEl = actionRef.current;
		if (!actionEl) {
			return;
		}

		const scheduleRetry = () => {
			if (typeof window === "undefined") {
				return;
			}
			if (retryTimeoutRef.current !== null) {
				return;
			}
			retryTimeoutRef.current = window.setTimeout(() => {
				retryTimeoutRef.current = null;
				updatePosition();
			}, 48);
		};

		const resolveScope = () => {
			const cached = scopeRef.current;
			if (cached && cached.isConnected && cached.contains(actionEl)) {
				return cached;
			}

			const scoped = actionEl.closest('[data-reply-scope="true"]');
			if (scoped) {
				return scoped;
			}

			return actionEl.parentElement || null;
		};

		const scopeEl = resolveScope();
		if (!scopeEl) {
			return;
		}

		scopeRef.current = scopeEl;

		let anchors = scopeEl.querySelectorAll('[data-reply-anchor="true"]');

		if (!anchors.length && lastAnchorRef.current && scopeEl.contains(lastAnchorRef.current)) {
			anchors = [lastAnchorRef.current];
		}

		let anchorEl = null;
		for (let index = 0; index < anchors.length; index += 1) {
			const candidate = anchors[index];
			if (!candidate) {
				continue;
			}

			const candidateStyle = window.getComputedStyle(candidate);
			const isHidden =
				candidateStyle.display === "none" ||
				candidateStyle.visibility === "hidden" ||
				Number(candidateStyle.opacity || "1") === 0 ||
				(candidate.offsetWidth === 0 && candidate.offsetHeight === 0);

			if (!isHidden) {
				anchorEl = candidate;
				break;
			}
		}

		if (!anchorEl) {
			anchorEl = anchors[0] || null;
		}

		const parentRect = scopeEl.getBoundingClientRect();
		const actionRect = actionEl.getBoundingClientRect();
		const horizontalGap = 24;
		const rawTop = isReplyMessage ? 0 : -16;
		const minLeft = 0;
		const maxLeft = Math.max(minLeft, parentRect.width - actionRect.width);

		if (!anchorEl) {
			scheduleRetry();
			const fallbackTarget = isSource
				? parentRect.width - actionRect.width - horizontalGap
				: horizontalGap;
			const fallbackLeft = Math.min(Math.max(fallbackTarget, minLeft), maxLeft);
			const fallbackStyle = {
				top: `${rawTop}px`,
				left: `${fallbackLeft}px`,
				right: "auto"
			};
			setInlineStyle((current) =>
				areStylesEqual(current, fallbackStyle) ? current : fallbackStyle
			);
			return;
		}

		lastAnchorRef.current = anchorEl;

		const anchorRect = anchorEl.getBoundingClientRect();
		const bubbleLeft = anchorRect.left - parentRect.left;
		const bubbleRight = anchorRect.right - parentRect.left;

		let computedLeft = isSource
			? bubbleRight - anchorRect.width - horizontalGap
			: bubbleLeft + anchorRect.width - horizontalGap - 56;
		const clampedLeft = Math.min(Math.max(computedLeft, minLeft), maxLeft);

		const nextStyle = {
			top: `${rawTop}px`,
			left: `${clampedLeft}px`,
			right: "auto"
		};

		setInlineStyle((current) =>
			areStylesEqual(current, nextStyle) ? current : nextStyle
		);
	}, [isSource, isReplyMessage]);

	const clearTouchHideTimeout = React.useCallback(() => {
		if (typeof window === "undefined") {
			return;
		}
		if (touchHideTimeoutRef.current !== null) {
			window.clearTimeout(touchHideTimeoutRef.current);
			touchHideTimeoutRef.current = null;
		}
	}, []);

	const scheduleTouchHide = React.useCallback(() => {
		if (typeof window === "undefined") {
			return;
		}
		clearTouchHideTimeout();
		touchHideTimeoutRef.current = window.setTimeout(() => {
			touchHideTimeoutRef.current = null;
			setIsTouchActive(false);
		}, 2000);
	}, [clearTouchHideTimeout]);

	const cancelLongPressDetection = React.useCallback(() => {
		if (typeof window === "undefined") {
			return;
		}
		if (longPressTimeoutRef.current !== null) {
			window.clearTimeout(longPressTimeoutRef.current);
			longPressTimeoutRef.current = null;
		}
	}, []);

	const announceActiveMenu = React.useCallback(() => {
		if (typeof window === "undefined") {
			return;
		}
		window.dispatchEvent(new CustomEvent("chatMessageReplyMenu:activated", { detail: { source: actionRef.current } }));
	}, []);

	React.useLayoutEffect(() => {
		if (actionRef.current) {
			updatePosition();
		}
	}, [updatePosition, isReplySelected]);

	React.useEffect(() => {
		isTouchActiveRef.current = isTouchActive;
	}, [isTouchActive]);

	React.useEffect(() => {
		if (actionRef.current) {
			updatePosition();
		}
	}, [updatePosition, anchorVersion]);

	React.useEffect(() => {
		if (typeof window === "undefined" || !actionRef.current) {
			return undefined;
		}

		window.addEventListener("resize", updatePosition);
		return () => window.removeEventListener("resize", updatePosition);
	}, [updatePosition]);

	React.useEffect(() => {
		const actionEl = actionRef.current;
		if (!actionEl) {
			return undefined;
		}

		const scopeEl = (scopeRef.current && scopeRef.current.isConnected && scopeRef.current.contains(actionEl))
			? scopeRef.current
			: actionEl.closest('[data-reply-scope="true"]') || actionEl.parentElement;

		if (!scopeEl) {
			return undefined;
		}

		scopeRef.current = scopeEl;

		const events = ["mouseenter", "mouseleave", "focusin", "focusout", "transitionend"]; // ensure reposition on hover-driven layout changes
		events.forEach((eventName) => scopeEl.addEventListener(eventName, updatePosition, true));
		updatePosition();

		return () => {
			events.forEach((eventName) => scopeEl.removeEventListener(eventName, updatePosition, true));
		};
	}, [updatePosition]);

	React.useEffect(() => {
		if (typeof window === "undefined") {
			return undefined;
		}

		const actionEl = actionRef.current;
		if (!actionEl) {
			return undefined;
		}

		const scopeEl = (scopeRef.current && scopeRef.current.isConnected && scopeRef.current.contains(actionEl))
			? scopeRef.current
			: actionEl.closest('[data-reply-scope="true"]') || actionEl.parentElement;

		if (!scopeEl) {
			return undefined;
		}

		scopeRef.current = scopeEl;

		const handleTouchStart = (event) => {
			clearTouchHideTimeout();
			cancelLongPressDetection();
			const touch = event.touches && event.touches[0];
			if (touch) {
				touchStartPositionRef.current = {
					x: touch.clientX,
					y: touch.clientY
				};
			} else {
				touchStartPositionRef.current = null;
			}
			longPressTimeoutRef.current = window.setTimeout(() => {
				longPressTimeoutRef.current = null;
				setIsTouchActive(true);
				announceActiveMenu();
				updatePosition();
			}, 1000);
			updatePosition();
		};

		const handleTouchMove = (event) => {
			if (!touchStartPositionRef.current) {
				return;
			}
			const touch = event.touches && event.touches[0];
			if (!touch) {
				return;
			}
			const deltaX = Math.abs(touch.clientX - touchStartPositionRef.current.x);
			const deltaY = Math.abs(touch.clientY - touchStartPositionRef.current.y);
			const threshold = 10;
			if (deltaX > threshold || deltaY > threshold) {
				cancelLongPressDetection();
				touchStartPositionRef.current = null;
				if (isTouchActiveRef.current) {
					setIsTouchActive(false);
					clearTouchHideTimeout();
				}
			}
		};

		const handleTouchEnd = () => {
			const wasPending = longPressTimeoutRef.current !== null;
			cancelLongPressDetection();
			touchStartPositionRef.current = null;
			if (wasPending) {
				setIsTouchActive(false);
				clearTouchHideTimeout();
			} else if (isTouchActiveRef.current) {
				scheduleTouchHide();
			} else {
				clearTouchHideTimeout();
			}
		};

		const handleTouchCancel = () => {
			cancelLongPressDetection();
			touchStartPositionRef.current = null;
			setIsTouchActive(false);
			clearTouchHideTimeout();
		};

		scopeEl.addEventListener("touchstart", handleTouchStart, true);
		scopeEl.addEventListener("touchmove", handleTouchMove, true);
		scopeEl.addEventListener("touchend", handleTouchEnd, true);
		scopeEl.addEventListener("touchcancel", handleTouchCancel, true);

		return () => {
			scopeEl.removeEventListener("touchstart", handleTouchStart, true);
			scopeEl.removeEventListener("touchmove", handleTouchMove, true);
			scopeEl.removeEventListener("touchend", handleTouchEnd, true);
			scopeEl.removeEventListener("touchcancel", handleTouchCancel, true);
			clearTouchHideTimeout();
			cancelLongPressDetection();
		};
	}, [announceActiveMenu, cancelLongPressDetection, clearTouchHideTimeout, scheduleTouchHide, updatePosition]);

	React.useEffect(() => {
		if (typeof window === "undefined") {
			return undefined;
		}

		const handleExternalActivation = (event) => {
			if (!actionRef.current) {
				return;
			}
			if (!event.detail || event.detail.source === actionRef.current) {
				return;
			}
			setIsTouchActive(false);
			clearTouchHideTimeout();
			cancelLongPressDetection();
		};

		window.addEventListener("chatMessageReplyMenu:activated", handleExternalActivation);

		return () => {
			window.removeEventListener("chatMessageReplyMenu:activated", handleExternalActivation);
		};
	}, [cancelLongPressDetection, clearTouchHideTimeout]);

	React.useEffect(() => {
		if (typeof window === "undefined") {
			return undefined;
		}

		const actionEl = actionRef.current;
		if (!actionEl) {
			return undefined;
		}

		const scopeEl = (scopeRef.current && scopeRef.current.isConnected && scopeRef.current.contains(actionEl))
			? scopeRef.current
			: actionEl.closest('[data-reply-scope="true"]') || actionEl.parentElement;

		if (!scopeEl) {
			return undefined;
		}

		scopeRef.current = scopeEl;

		const resizeObserver = typeof window.ResizeObserver !== "undefined"
			? new window.ResizeObserver(() => updatePosition())
			: null;
		const mutationObserver = typeof window.MutationObserver !== "undefined"
			? new window.MutationObserver(() => updatePosition())
			: null;

		if (resizeObserver) {
			resizeObserver.observe(scopeEl);
			const anchors = scopeEl.querySelectorAll('[data-reply-anchor="true"]');
			anchors.forEach((anchor) => resizeObserver.observe(anchor));
		}

		if (mutationObserver) {
			mutationObserver.observe(scopeEl, { childList: true, subtree: true });
		}

		updatePosition();

		return () => {
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
			if (mutationObserver) {
				mutationObserver.disconnect();
			}
		};
	}, [updatePosition, isSource]);

	React.useEffect(() => () => {
		if (typeof window === "undefined") {
			return;
		}
		if (retryTimeoutRef.current !== null) {
			window.clearTimeout(retryTimeoutRef.current);
			retryTimeoutRef.current = null;
		}
		if (touchHideTimeoutRef.current !== null) {
			window.clearTimeout(touchHideTimeoutRef.current);
			touchHideTimeoutRef.current = null;
		}
		if (longPressTimeoutRef.current !== null) {
			window.clearTimeout(longPressTimeoutRef.current);
			longPressTimeoutRef.current = null;
		}
		touchStartPositionRef.current = null;
	}, []);

	const handleAction = React.useCallback(
		(event) => {
			if (event) {
				event.preventDefault();
				event.stopPropagation();
			}

			if (!canReply || typeof onReply !== "function") {
				return;
			}

			onReply(event);
			setIsTouchActive(false);
			clearTouchHideTimeout();
			cancelLongPressDetection();
		},
		[canReply, onReply, cancelLongPressDetection, clearTouchHideTimeout]
	);

	if (!canReply) {
		return null;
	}

	const classNames = ["reply-action", isSource ? "-source" : "-destination"];
	if (isTouchActive) {
		classNames.push("-active");
	}

	const labelText = replyLabel || "__reply";

	return (
		<div
			className={classNames.join(" ")}
			ref={actionRef}
			style={inlineStyle || undefined}
			role="button"
			tabIndex={0}
			onClick={handleAction}
		>
			<i style={{ fontSize: 14, marginRight: 4 }} className="icon icon-fluent_arrow-reply"></i>
			<span>{labelText}</span>
		</div>
	);
}
